const DEFAULT_API_ERROR_MESSAGE = "Error";

export const SERVER_HTML_ERROR_MESSAGE =
  "Server error. The server returned an HTML page instead of a response.";

export function isHtmlErrorBody(value) {
  if (typeof value !== "string") return false;
  const head = value.trim().slice(0, 400).toLowerCase();
  if (!head) return false;
  if (head.startsWith("<!doctype html")) return true;
  if (head.startsWith("<html")) return true;
  return head.includes("<html") && head.includes("<head");
}

function tryParseJson(text) {
  if (typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

const MESSAGE_KEYS = [
  "detail",
  "details",
  "error",
  "message",
  "error_message",
  "msg",
  "description",
];

function isUnusableMessage(value) {
  if (value == null) return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    !trimmed ||
    trimmed === "[object Object]" ||
    trimmed.includes("[object Object]")
  );
}

/**
 * Pulls a human-readable string out of detail / error / nested JSON.
 * Never returns a raw object or "[object Object]".
 */
function extractMessageFromValue(value, depth = 0) {
  if (value == null || depth > 5) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (isUnusableMessage(trimmed)) return null;

    const parsed = tryParseJson(trimmed);
    if (parsed != null && typeof parsed === "object") {
      const nested = extractMessageFromValue(parsed, depth + 1);
      if (nested) return nested;
    }

    return trimmed;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return formatValidationErrorsFromBody(value);
  }

  if (typeof value === "object") {
    for (const key of MESSAGE_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      const nested = extractMessageFromValue(value[key], depth + 1);
      if (nested) return nested;
    }
    if (Array.isArray(value.errors)) {
      return formatValidationErrorsFromBody(value.errors);
    }
    return null;
  }

  return null;
}

/**
 * FastAPI / Pydantic style: `{ detail: [{ loc, msg, type }, ...] }`.
 * @returns {string|null}
 */
export function formatValidationErrorsFromBody(body) {
  if (Array.isArray(body)) {
    return formatValidationErrorsFromBody({ detail: body });
  }
  if (body == null || typeof body !== "object") return null;
  const detail = body.detail ?? body.details ?? body.errors;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const loc = Array.isArray(item.loc)
            ? item.loc.filter((x) => x != null && String(x).trim() !== "").join(" → ")
            : "";
          const msg = item.ctx?.error || item.msg || item.message || "";
          if (loc && msg) return `${loc}: ${msg}`;
          return msg || JSON.stringify(item);
        }
        return String(item);
      })
      .filter(Boolean);
    return parts.length ? parts.join("; ") : null;
  }
  if (typeof detail === "string" && detail.trim() !== "") return detail;
  if (typeof body.message === "string" && body.message.trim() !== "") {
    return body.message;
  }
  return null;
}

/**
 * Formats an API response body without discarding server information.
 * `detail` always has priority; then a plain `error`/`message` string;
 * otherwise the complete returned body is used.
 */
export function formatApiErrorBody(body, fallback = DEFAULT_API_ERROR_MESSAGE) {
  return extractMessageFromValue(body) || fallback;
}

/**
 * Reads an error response through a clone so the original body remains
 * available to existing callers.
 */
export async function getResponseErrorMessage(
  response,
  fallback = DEFAULT_API_ERROR_MESSAGE
) {
  if (!response) return fallback;

  try {
    const text = await response.clone().text();
    return formatApiErrorBody(text, fallback);
  } catch {
    return fallback;
  }
}

export function parseServerError(serverErr) {
  return getApiErrorMessage(serverErr, DEFAULT_API_ERROR_MESSAGE);
}

/**
 * Subscription upsell payload. Permission 403s also have a message/detail
 * and must not be treated as "buy a plan".
 */
export function isSubscriptionRequiredPayload(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return false;
  }
  return Boolean(payload.to_buy);
}

export function getApiErrorMessage(error, fallback = DEFAULT_API_ERROR_MESSAGE) {
  const data =
    error?.response?.data ??
    (typeof error?.payload === "string" || Array.isArray(error?.payload) || (error?.payload != null && typeof error.payload === "object")
      ? error.payload
      : undefined) ??
    error?.data ??
    error;

  if (Array.isArray(data)) {
    return formatApiErrorBody(data, fallback);
  }

  if (typeof data === "string") {
    return formatApiErrorBody(data, fallback);
  }

  if (data === error && error instanceof Error) {
    const message = typeof error.message === "string" ? error.message.trim() : "";
    return isUnusableMessage(message)
      ? fallback
      : formatApiErrorBody(message, fallback);
  }

  if (data != null && typeof data === "object") {
    return formatApiErrorBody(data, fallback);
  }

  if (typeof error?.payload === "string" && error.payload.trim() !== "") {
    return formatApiErrorBody(error.payload, fallback);
  }

  if (error instanceof Error && typeof error.message === "string" && error.message.trim() !== "") {
    return isUnusableMessage(error.message)
      ? fallback
      : formatApiErrorBody(error.message, fallback);
  }

  return fallback;
}
